import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { NgClass } from '@angular/common';
import { ModalComponent } from '../../../shared/components/modal/modal';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin, Observable } from 'rxjs';
import { RollupService } from '../../../core/services/rollup.service';
import { TaxonomyService } from '../../../core/services/taxonomy.service';
import { TaxonomyMemberService } from '../../../core/services/taxonomy-member.service';
import { ToastService } from '../../../core/services/toast.service';
import { RollupCategoryDetail } from '../../../shared/models/rollup.models';
import {
  SituationTaxonomy,
  SituationTaxonomiesResponse,
  ResponseTaxonomy,
  ResponseTaxonomiesResponse,
} from '../../../shared/models/taxonomy.models';
import {
  SituationTaxonomyMembersResponse,
  ResponseTaxonomyMembersResponse,
} from '../../../shared/models/taxonomy-member.models';
import { apiErrorMessage } from '../../../shared/utils/api-error';

interface SourceCategory {
  id: string;
  name: string;
}

interface SourceGroup {
  taxonomyId: string;
  taxonomyName: string;
  categories: SourceCategory[];
  expanded: boolean;
}

@Component({
  selector: 'app-rollup-detail',
  imports: [NgClass, ModalComponent],
  templateUrl: './rollup-detail.html',
})
export class RollupDetailComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private rollupService = inject(RollupService);
  private taxonomyService = inject(TaxonomyService);
  private taxonomyMemberService = inject(TaxonomyMemberService);
  private toastService = inject(ToastService);
  private destroyRef = inject(DestroyRef);

  protected type = signal<'situation' | 'response'>('situation');
  protected rollup = signal<RollupCategoryDetail | null>(null);
  protected loading = signal(true);
  protected loadingSource = signal(false);
  protected editMode = signal(false);
  protected saving = signal(false);
  protected confirmDelete = signal(false);
  protected deleting = signal(false);
  protected sourceGroups = signal<SourceGroup[]>([]);
  protected checkedIds = signal<string[]>([]);

  protected mappedCategories = computed(() => {
    const ids = new Set(this.checkedIds());
    const seen = new Set<string>();
    return this.sourceGroups()
      .flatMap((g) => g.categories)
      .filter((c) => {
        if (!ids.has(c.id) || seen.has(c.id)) return false;
        seen.add(c.id);
        return true;
      });
  });

  ngOnInit(): void {
    const type = this.route.snapshot.paramMap.get('type') as 'situation' | 'response';
    const id = this.route.snapshot.paramMap.get('id') ?? '';
    this.type.set(type);

    const detail$ =
      type === 'situation'
        ? this.rollupService.getRollupSituationCategoryById(id)
        : this.rollupService.getRollupResponseCategoryById(id);

    detail$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (detail) => {
        this.rollup.set(detail);
        this.checkedIds.set(detail.categories.map((c) => c.id));
        this.loading.set(false);
        this.loadSourceLibrary();
      },
      error: () => this.loading.set(false),
    });
  }

  private loadSourceLibrary(): void {
    this.loadingSource.set(true);
    const type = this.type();

    const taxonomies$: Observable<SituationTaxonomiesResponse | ResponseTaxonomiesResponse> =
      type === 'situation'
        ? this.taxonomyService.getSituationTaxonomies()
        : this.taxonomyService.getResponseTaxonomies();

    taxonomies$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: SituationTaxonomiesResponse | ResponseTaxonomiesResponse) => {
        const taxonomies: { id: string; name: string }[] =
          type === 'situation'
            ? ((res as any).situationTaxonomies?.map((t: SituationTaxonomy) => ({
                id: t.id,
                name: t.name,
              })) ?? [])
            : ((res as any).responseTaxonomies?.map((t: ResponseTaxonomy) => ({
                id: t.id,
                name: t.taxonomyName,
              })) ?? []);

        if (!taxonomies.length) {
          this.loadingSource.set(false);
          return;
        }

        const memberRequests$ = taxonomies.map((t) =>
          type === 'situation'
            ? this.taxonomyMemberService.getSituationTaxonomyMembers(t.id, {
                pageSize: 1000,
                pageIndex: 0,
              })
            : this.taxonomyMemberService.getResponseTaxonomyMembers(t.id, {
                pageSize: 1000,
                pageIndex: 0,
              }),
        );

        forkJoin(memberRequests$)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: (results) => {
              const groups: SourceGroup[] = taxonomies
                .map((t, i) => {
                  let categories: SourceCategory[];
                  if (type === 'situation') {
                    const r = results[i] as SituationTaxonomyMembersResponse;
                    categories = (r.situationTaxonomiesMembers ?? []).map((m) => ({
                      id: m.situationCategoryId,
                      name: m.situationCategoryName,
                    }));
                  } else {
                    const r = results[i] as ResponseTaxonomyMembersResponse;
                    categories = (r.responseTaxonomiesMembers ?? []).map((m) => ({
                      id: m.responseCategory.id,
                      name: m.responseCategory.name,
                    }));
                  }
                  return {
                    taxonomyId: t.id,
                    taxonomyName: t.name,
                    categories,
                    expanded: categories.length > 0,
                  };
                })
                .filter((g) => g.categories.length > 0);

              this.sourceGroups.set(groups);
              this.loadingSource.set(false);
            },
            error: () => this.loadingSource.set(false),
          });
      },
      error: () => this.loadingSource.set(false),
    });
  }

  protected toggleGroup(taxonomyId: string): void {
    this.sourceGroups.update((groups) =>
      groups.map((g) => (g.taxonomyId === taxonomyId ? { ...g, expanded: !g.expanded } : g)),
    );
  }

  protected isChecked(categoryId: string): boolean {
    return this.checkedIds().includes(categoryId);
  }

  protected toggleCategory(categoryId: string): void {
    if (!this.editMode()) return;
    this.checkedIds.update((ids) =>
      ids.includes(categoryId) ? ids.filter((id) => id !== categoryId) : [...ids, categoryId],
    );
  }

  protected removeCategory(categoryId: string): void {
    this.checkedIds.update((ids) => ids.filter((id) => id !== categoryId));
  }

  protected onEdit(): void {
    this.editMode.set(true);
  }

  protected onCancel(): void {
    const rollup = this.rollup();
    if (rollup) this.checkedIds.set(rollup.categories.map((c) => c.id));
    this.editMode.set(false);
  }

  protected onSave(): void {
    const rollup = this.rollup();
    if (!rollup) return;

    this.saving.set(true);
    const mapped = this.mappedCategories();

    // Derive which taxonomies are sources (those that have at least one checked category)
    const activeTaxonomyIds = new Set(
      this.sourceGroups()
        .filter((g) => g.categories.some((c) => this.isChecked(c.id)))
        .map((g) => g.taxonomyId),
    );
    const taxonomies = this.sourceGroups()
      .filter((g) => activeTaxonomyIds.has(g.taxonomyId))
      .map((g) => ({ id: g.taxonomyId, name: g.taxonomyName }));

    const payload: RollupCategoryDetail = {
      ...rollup,
      taxonomies,
      categories: mapped.map((c) => ({ id: c.id, name: c.name })),
    };

    const request$ =
      this.type() === 'situation'
        ? this.rollupService.updateRollupSituationCategory(payload)
        : this.rollupService.updateRollupResponseCategory(payload);

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (updated) => {
        this.rollup.set(updated);
        this.toastService.show('Rollup saved successfully');
        this.editMode.set(false);
        this.saving.set(false);
      },
      error: (err) => {
        this.toastService.show(apiErrorMessage(err, 'Failed to save rollup'), 'error');
        this.saving.set(false);
      },
    });
  }

  protected onDelete(): void {
    const rollup = this.rollup();
    if (!rollup) return;

    this.deleting.set(true);
    const request$ =
      this.type() === 'situation'
        ? this.rollupService.deleteRollupSituationCategory(rollup.id)
        : this.rollupService.deleteRollupResponseCategory(rollup.id);

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.toastService.show('Rollup deleted successfully');
        this.router.navigate(['/configuration/rollup-taxonomies']);
      },
      error: (err) => {
        this.toastService.show(apiErrorMessage(err, 'Failed to delete rollup'), 'error');
        this.deleting.set(false);
        this.confirmDelete.set(false);
      },
    });
  }

  protected goBack(): void {
    this.router.navigate(['/configuration/rollup-taxonomies']);
  }
}
